import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, ArrowUpRight } from 'lucide-react';
import Logo from './components/Logo';
import {
  useLanguage,
  SUPPORTED_LOCALES,
  SupportedLocale,
} from './hooks/useLanguage';
import content from './locales/content';
import posthog from './posthog';

type WaitlistTexts = {
  placeholder: string;
  submit: string;
  success: string;
  error: string;
  label: string;
};

function MailingListForm({
  variant = 'hero',
  texts,
}: {
  variant?: 'hero' | 'compact';
  texts: WaitlistTexts;
}) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [loading, setLoading] = useState(false);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      return;
    }
    setLoading(true);
    try {
      posthog?.capture?.('waitlist_signup', {
        email,
        source: variant,
      });
      setEmail('');
      setStatus('success');
    } catch (err) {
      console.error('Failed to capture waitlist signup', err);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const inputClasses =
    variant === 'compact'
      ? 'flex-1 rounded-full border border-pink-200 bg-white/90 px-3 py-2 text-sm focus:border-pink-400 focus:ring-2 focus:ring-pink-200'
      : 'rounded-full border border-pink-200 bg-white/90 px-5 text-base focus:border-pink-400 focus:ring-2 focus:ring-pink-200 h-14 flex items-center';

  const buttonClasses =
    variant === 'compact'
      ? 'inline-flex items-center justify-center rounded-full bg-pink-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-pink-700 disabled:opacity-60'
      : 'inline-flex items-center justify-center rounded-full bg-pink-600 px-6 h-14 text-base font-semibold text-white transition hover:bg-pink-700 disabled:opacity-60';

  return (
    <div
      className={
        variant === 'compact'
          ? 'w-full max-w-sm'
          : 'w-full flex flex-col items-center'
      }
    >
      <form
        onSubmit={handleSubmit}
        className={`flex gap-3 ${
          variant === 'compact'
            ? 'items-stretch flex-nowrap'
            : 'items-center flex-col sm:flex-row sm:flex-nowrap sm:justify-center'
        }`}
      >
        <input
          type="email"
          name="email"
          aria-label={texts.label}
          placeholder={texts.placeholder}
          className={`${inputClasses} ${
            variant === 'hero' ? 'w-full sm:w-[360px]' : 'max-w-[360px] w-full'
          }`}
          value={email}
          onChange={event => {
            setEmail(event.target.value);
            if (status !== 'idle') setStatus('idle');
          }}
          disabled={loading}
          required
        />
        <button
          type="submit"
          disabled={loading}
          className={`${buttonClasses} whitespace-nowrap`}
        >
          {loading ? '…' : texts.submit}
        </button>
      </form>
      <p
        className={`mt-2 text-xs ${
          status === 'success'
            ? 'text-emerald-600'
            : status === 'error'
              ? 'text-red-600'
              : 'text-gray-500'
        }`}
      >
        {status === 'success'
          ? texts.success
          : status === 'error'
            ? texts.error
            : ''}
      </p>
    </div>
  );
}

type LanguageToggleProps = {
  locale: SupportedLocale;
  onChange: (locale: SupportedLocale) => void;
  label: string;
};

function LanguageToggle({ locale, onChange, label }: LanguageToggleProps) {
  return (
    <div
      className="inline-flex items-center rounded-full border border-pink-200 bg-white/80 p-1 text-xs font-semibold text-gray-600 shadow-sm"
      role="group"
      aria-label={label}
    >
      {SUPPORTED_LOCALES.map(code => (
        <button
          key={code}
          type="button"
          aria-pressed={locale === code}
          onClick={() => {
            if (locale !== code) onChange(code);
          }}
          className={`px-3 py-1 rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 ${
            locale === code
              ? 'bg-pink-600 text-white shadow'
              : 'text-gray-600 hover:text-pink-600'
          }`}
        >
          {code === 'en' ? 'EN' : 'PT-BR'}
        </button>
      ))}
    </div>
  );
}

function TypewriterText({ text }: { text?: string }) {
  const [display, setDisplay] = useState('');

  useEffect(() => {
    const target = (text ?? '').trim();
    if (!target) {
      setDisplay('');
      return;
    }

    setDisplay('');
    let i = 0;
    let timeout: number | null = null;

    const step = () => {
      i += 1;
      setDisplay(target.slice(0, i));
      if (i < target.length) {
        timeout = window.setTimeout(step, 8);
      }
    };

    timeout = window.setTimeout(step, 0);

    return () => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
    };
  }, [text]);

  return (
    <div className="font-mono text-[13px] leading-relaxed text-gray-900 whitespace-pre-line">
      {display}
    </div>
  );
}

const LIKE_KEY = 'promptilia-like';

function App() {
  const { locale, setLocale } = useLanguage();
  const copy = content[locale];
  const detailedFeatures = copy.detailedFeatures;
  const typewriterContent = copy.typewriter;
  const steps = copy.steps;
  const sections = copy.sections;
  const [promptStage, setPromptStage] = useState<
    'initial' | 'merging' | 'done'
  >('initial');
  const [hoveredFeature, setHoveredFeature] = useState<string | null>(null);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    if (promptStage === 'merging') {
      const timer = setTimeout(() => {
        setPromptStage('done');
      }, 1800);
      return () => clearTimeout(timer);
    }
  }, [promptStage]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(LIKE_KEY);
    if (stored === 'true') {
      setLiked(true);
    }
  }, []);

  const toggleLike = () => {
    setLiked(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LIKE_KEY, next ? 'true' : 'false');
      }
      posthog?.capture?.('promptilia_like', {
        state: next ? 'liked' : 'unliked',
      });
      return next;
    });
  };

  const handleEnhance = () => {
    if (promptStage !== 'initial') return;
    setPromptStage('merging');
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#fff8fb] text-gray-900">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10 sm:py-12 space-y-16 sm:space-y-20">
        <header className="flex flex-col gap-10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex w-full items-center justify-between gap-4">
              <Logo variant="full" size="lg" className="scale-105" />
              <LanguageToggle
                locale={locale}
                onChange={setLocale}
                label={copy.languageToggleLabel}
              />
            </div>
            <MailingListForm variant="compact" texts={copy.waitlist} />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-6 relative"
          >
            <div className="absolute inset-x-1/2 -z-10 h-48 w-56 -translate-x-1/2 blur-3xl rounded-full bg-pink-200/40" />
            <p className="text-xs tracking-[0.35em] uppercase text-pink-500">
              {copy.hero.eyebrow}
            </p>
            <h1 className="text-[3.6rem] leading-tight font-semibold text-gray-950">
              {copy.hero.title}
            </h1>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed">
              {copy.hero.subtitle}
            </p>
            <p className="text-sm text-gray-600 max-w-2xl mx-auto">
              {copy.hero.description}
            </p>
            <div className="flex flex-col items-center gap-4">
              <div className="flex w-full max-w-2xl flex-col items-center gap-3 sm:flex-row sm:flex-nowrap sm:justify-center">
                <MailingListForm variant="hero" texts={copy.waitlist} />
                <button
                  type="button"
                  onClick={toggleLike}
                  aria-pressed={liked}
                  className="inline-flex w-full items-center justify-center gap-2 px-6 h-14 rounded-full border border-pink-200 text-pink-700 hover:bg-pink-50 transition whitespace-nowrap sm:w-auto"
                >
                  <span aria-hidden="true">{liked ? '❤️' : '🤍'}</span>
                  {liked ? copy.likeThanks : copy.ctaSecondary}
                </button>
              </div>
            </div>
          </motion.div>

          <section className="space-y-6">
            <div className="max-w-[1400px] mx-auto border-t border-pink-200 pt-8 sm:pt-12">
              <div className="grid gap-8 relative transition-all grid-cols-1 md:grid-cols-12 items-start">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{
                    opacity: promptStage === 'done' ? 0.7 : 1,
                    y: 0,
                    x: promptStage === 'initial' ? 0 : 0,
                  }}
                  transition={{ duration: 0.6 }}
                  className={`space-y-3 ${
                    promptStage === 'initial'
                      ? 'max-w-3xl mx-auto md:col-span-12'
                      : 'md:col-span-4 lg:col-span-3'
                  }`}
                >
                  <p className="text-[11px] uppercase tracking-[0.45em] text-gray-400">
                    {copy.status.before}
                  </p>
                  <motion.div
                    whileHover={{ y: -6 }}
                    className={`rounded-3xl border border-pink-200 bg-[#fffafb] p-7 text-gray-800 shadow-[0_30px_60px_rgba(255,182,193,0.18)] whitespace-pre-line leading-relaxed font-mono space-y-5 ${
                      promptStage === 'initial' ? 'text-base' : 'text-xs'
                    }`}
                  >
                    {copy.samplePrompt}
                    <div className="pt-4">
                      <div className="relative inline-flex">
                        {promptStage === 'initial' && (
                          <span
                            aria-hidden="true"
                            className="absolute inset-0 rounded-full border border-pink-200 animate-ping"
                          />
                        )}
                        <motion.button
                          onClick={handleEnhance}
                          disabled={promptStage !== 'initial'}
                          initial={{ opacity: 0, scale: 0.85, y: 10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          transition={{
                            duration: 0.6,
                            delay: 0.3,
                            type: 'spring',
                            stiffness: 220,
                            damping: 18,
                          }}
                          className="relative inline-flex items-center justify-center gap-3 rounded-full border border-pink-500 px-5 py-2.5 text-sm font-medium text-pink-600 hover:bg-pink-50 transition disabled:cursor-not-allowed disabled:opacity-60 shadow-[0_15px_30px_rgba(255,182,193,0.35)]"
                          style={{ minWidth: '12rem' }}
                        >
                          <span aria-hidden="true">✨</span>
                          {copy.status.enhanceButton}
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
                {promptStage !== 'initial' && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={
                      promptStage === 'done'
                        ? { opacity: 0.7, y: 0 }
                        : { opacity: 1, y: 0, x: 0 }
                    }
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="space-y-3 md:-ml-2 md:col-span-2 lg:col-span-2"
                  >
                    <p className="text-[11px] uppercase tracking-[0.45em] text-pink-500 whitespace-nowrap">
                      {promptStage === 'merging'
                        ? copy.status.enhancing
                        : copy.status.ready}
                    </p>
                    <motion.div
                      animate={
                        promptStage === 'merging'
                          ? { y: [-3, 3, -3] }
                          : { y: 0 }
                      }
                      transition={
                        promptStage === 'merging'
                          ? { repeat: Infinity, duration: 1.5 }
                          : undefined
                      }
                      className="rounded-3xl border border-pink-200 bg-white p-8 text-gray-900 shadow-[0_30px_60px_rgba(255,182,193,0.22)] space-y-5"
                    >
                      {promptStage === 'merging' ? (
                        <div className="flex flex-col items-center justify-center py-6 text-pink-500 space-y-3">
                          <div className="h-1 w-full max-w-[150px] overflow-hidden rounded-full bg-pink-100">
                            <div className="h-full w-1/2 animate-[pulse_1.5s_ease-in-out_infinite] bg-pink-400" />
                          </div>
                          <p className="text-xs tracking-[0.2em] uppercase text-center">
                            {copy.status.enhancingMessage}
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-6 space-y-3 text-center">
                          <div className="h-12 w-12 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 text-xl">
                            ✓
                          </div>
                          <p className="text-sm tracking-[0.25em] uppercase text-pink-500">
                            {copy.status.enhancedMessage}
                          </p>
                        </div>
                      )}
                    </motion.div>
                  </motion.div>
                )}
                {promptStage === 'done' && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="space-y-3 md:ml-4 md:col-span-6 lg:col-span-7"
                  >
                    <p className="text-xs uppercase tracking-[0.45em] text-gray-900 flex items-center gap-2">
                      {copy.status.afterLabel}
                      <ArrowUpRight className="h-4 w-4 text-pink-500" />
                    </p>
                    <motion.div
                      whileHover={{ y: -6 }}
                      className="rounded-3xl border border-pink-300 bg-pink-100/25 p-10 text-gray-900 shadow-[0_60px_110px_rgba(255,182,193,0.35)] space-y-6 text-base leading-relaxed font-mono"
                    >
                      <TypewriterText text={typewriterContent} />
                    </motion.div>
                  </motion.div>
                )}
              </div>
            </div>
          </section>
        </header>

        <div className="max-w-[1400px] mx-auto py-8 sm:py-12 text-center space-y-3">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-pink-200 to-transparent mb-12 sm:mb-20" />
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.35em] text-pink-400">
              {sections.howItWorksEyebrow}
            </p>
            <h2 className="text-3xl font-semibold text-gray-900">
              {sections.howItWorksTitle}
            </h2>
            <p className="text-gray-700 leading-relaxed max-w-3xl mx-auto">
              {sections.howItWorksDescription}
            </p>
          </div>
        </div>

        <section className="grid md:grid-cols-2 gap-8 md:gap-10" id="story">
          <div className="space-y-5">
            <p className="text-xs uppercase tracking-[0.35em] text-pink-400">
              {sections.storyEyebrow}
            </p>
            <h3 className="text-2xl font-semibold text-gray-900">
              {sections.storyTitle}
            </h3>
            <p className="text-gray-800 leading-relaxed text-lg">
              {sections.storyDescription}
            </p>
          </div>
          <div className="rounded-3xl border border-pink-200 bg-white p-12 space-y-6 shadow-[0_30px_60px_rgba(255,182,193,0.2)]">
            {steps.map(step => (
              <div key={step.title} className="space-y-1 text-gray-800">
                <p className="font-semibold text-gray-900">{step.title}</p>
                <p className="text-sm leading-relaxed text-gray-700">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        <div className="h-px w-full bg-gradient-to-r from-transparent via-pink-200 to-transparent" />

        <section id="product" className="space-y-6">
          <div className="space-y-3 text-center">
            <p className="text-xs uppercase tracking-[0.35em] text-pink-400">
              {sections.productEyebrow}
            </p>
            <h2 className="text-3xl font-semibold text-gray-900">
              {sections.productTitle}
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto leading-relaxed">
              {sections.productDescription}
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {detailedFeatures.map((item, index) => {
              const tooltipSide = index % 2 === 0 ? 'left' : 'right';
              const sidePositionClasses =
                tooltipSide === 'left'
                  ? 'md:left-auto md:right-full md:mr-4 md:top-1/2 md:-translate-y-1/2 md:translate-x-0'
                  : 'md:right-auto md:left-full md:ml-4 md:top-1/2 md:-translate-y-1/2 md:translate-x-0';
              const detailHeading = copy.featureDetailLabel.replace(
                '{title}',
                item.title
              );

              return (
                <div
                  key={item.title}
                  className="relative rounded-3xl border border-pink-200 bg-white p-6 shadow-[0_20px_34px_rgba(255,182,193,0.2)] space-y-2 group focus-within:ring-2 focus-within:ring-pink-200"
                  tabIndex={0}
                  onMouseEnter={() => setHoveredFeature(item.title)}
                  onMouseLeave={() => setHoveredFeature(null)}
                  onFocus={() => setHoveredFeature(item.title)}
                  onBlur={() => setHoveredFeature(null)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl" aria-hidden="true">
                      {item.icon}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {item.description}
                  </p>
                  <motion.div
                    role="tooltip"
                    aria-hidden={hoveredFeature === item.title ? false : true}
                    initial={false}
                    animate={
                      hoveredFeature === item.title
                        ? { opacity: 1, y: 0 }
                        : { opacity: 0, y: 12 }
                    }
                    transition={{ duration: 0.2 }}
                    className={`pointer-events-none absolute left-1/2 top-full z-10 mt-4 w-72 -translate-x-1/2 rounded-2xl border border-pink-200 bg-white/95 p-4 text-sm text-gray-800 shadow-xl backdrop-blur-md ${sidePositionClasses}`}
                  >
                    <p className="font-medium text-pink-600 mb-1">
                      {detailHeading}
                    </p>
                    <p className="leading-relaxed">{item.detail}</p>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="h-px w-full bg-gradient-to-r from-transparent via-pink-200 to-transparent" />

        <section className="text-center space-y-3">
          <p className="text-xs uppercase tracking-[0.35em] text-pink-400">
            {sections.finalEyebrow}
          </p>
          <Heart className="h-6 w-6 text-pink-500 mx-auto" />
          <p className="text-sm font-semibold text-pink-600">
            {sections.clipboardTitle}
          </p>
          <h2 className="text-2xl font-semibold text-gray-900">
            {sections.finalTitle}
          </h2>
          <p className="text-gray-700 max-w-2xl mx-auto leading-relaxed">
            {sections.finalDescription}
          </p>
          <p className="text-sm text-gray-600">{sections.clipboardDescription}</p>
          <div className="flex justify-center">
            <MailingListForm variant="compact" texts={copy.waitlist} />
          </div>
        </section>

        <footer className="border-t border-pink-100 pt-8 text-sm text-gray-500 text-center">
          {copy.footer.note}
        </footer>
      </div>
    </div>
  );
}

export default App;
